import React, { useState, useEffect } from 'react';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import './V3TicketForm.scss';

interface EventField {
  id: number;
  name: string;
  label: string;
  field_type: string;
  placeholder?: string;
  help_text?: string;
  required: boolean;
  options?: string;
  serial_order: number;
  validation_type: string;
  min_value?: string;
  max_value?: string;
  validation_regex?: string;
  validation_error_message?: string;
}

interface Category {
  id: number;
  name: string;
  price: number;
  price_display: string;
  description: string;
  quantity: number;
  sold_count: number;
  remaining: number;
  sold_out: boolean;
  hidden: boolean;
  highlight: boolean;
  registration_type: string;
  tag: string;
  priority: number;
  currency?: {
    symbol: string;
  };
  show_any_amount?: boolean;
}

interface V3TicketFormProps {
  event: any;
  categories?: Category[];
  quota?: number;
  loading?: boolean;
  error?: string | null;
}

const V3TicketForm: React.FC<V3TicketFormProps> = ({ 
  event, 
  categories = [], 
  quota = 0,
  loading = false, 
  error = null 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [additionalAttendees, setAdditionalAttendees] = useState<Array<{
    name: string;
    email: string;
    phone: string;
    custom_fields: Record<string, string>;
  }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Get visible categories (not hidden)
  const visibleCategories = categories.filter(cat => !cat.hidden);

  useEffect(() => {
    // Auto-select if only one category
    if (visibleCategories.length === 1) {
      setSelectedCategory(visibleCategories[0]);
    }
  }, [visibleCategories]);

  // Auto-fill form data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const phoneNumber = user.profile?.phone_number || '';
      
      setFormData(prev => ({
        ...prev,
        name: prev.name || fullName,
        email: prev.email || user.email,
        phone: prev.phone || phoneNumber,
      }));
    }
  }, [isAuthenticated, user]);

  // Manage additional attendees when quantity changes
  useEffect(() => {
    if (event?.setting?.collect_individual_information && quantity > 1) {
      const neededAttendees = quantity - 1;
      setAdditionalAttendees(prev => {
        const newAttendees = [...prev];
        while (newAttendees.length < neededAttendees) {
          newAttendees.push({
            name: '',
            email: '',
            phone: '',
            custom_fields: {}
          });
        }
        return newAttendees.slice(0, neededAttendees);
      });
    } else {
      setAdditionalAttendees([]);
    }
  }, [quantity, event?.setting?.collect_individual_information]);

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCustomFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAdditionalAttendeeChange = (index: number, field: string, value: string) => {
    setAdditionalAttendees(prev => prev.map((attendee, i) => 
      i === index ? { ...attendee, [field]: value } : attendee
    ));
  };

  const handleAdditionalAttendeeCustomFieldChange = (index: number, fieldName: string, value: string) => {
    setAdditionalAttendees(prev => prev.map((attendee, i) => 
      i === index ? { 
        ...attendee, 
        custom_fields: { ...attendee.custom_fields, [fieldName]: value }
      } : attendee
    ));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuantity(parseInt(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    if (event?.setting?.collect_individual_information && quantity > 1) {
      for (let i = 0; i < additionalAttendees.length; i++) {
        const attendee = additionalAttendees[i];
        if (!attendee.name || !attendee.email || !attendee.phone) {
          setSubmitError(`Please fill in all required fields for attendee ${i + 2}`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const registrationData = {
        event_slug: event.slug,
        event_pass_category: selectedCategory.id,
        quantity,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        custom_fields: customFields,
        ...(event?.setting?.collect_individual_information && quantity > 1 && {
          additional_attendees: additionalAttendees
        })
      };

      const response = await apiService.registration.onpage(registrationData);

      if (response.success) {
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        } else {
          // Use window.location instead of navigate to avoid router context issues
          window.location.href = '/cart/';
        }
      } else {
        setSubmitError(response.error || 'Registration failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.response?.data?.error) {
        setSubmitError(error.response.data.error);
      } else if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        setSubmitError(errorMessages.join(', '));
      } else {
        setSubmitError('An error occurred during registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (category: Category) => {
    if (category.price === 0) {
      return 'Free';
    }
    
    if (category.show_any_amount) {
      return category.price_display || `$${category.price.toFixed(2)}`;
    }
    
    const symbol = category.currency?.symbol || event?.setting?.currency?.symbol || '$';
    return `${symbol} ${category.price.toLocaleString()}`;
  };

  const renderCustomField = (field: EventField) => {
    const fieldName = field.name;
    const value = customFields[fieldName] || '';

    if (field.field_type === 'select') {
      const options = field.options ? field.options.split('\n') : [];
      return (
        <div key={field.id} className="form-group">
          <label htmlFor={fieldName}>
            {field.label} {field.required && <span className="text-danger">*</span>}
          </label>
          <select
            className="form-select"
            name={fieldName}
            id={fieldName}
            value={value}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {options.map((option, index) => (
              <option key={index} value={option.trim()}>{option.trim()}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.id} className="form-group">
        <label htmlFor={fieldName}>
          {field.label} {field.required && <span className="text-danger">*</span>}
        </label>
        <input
          type={field.field_type}
          className="form-control"
          name={fieldName}
          id={fieldName}
          placeholder={field.placeholder || field.label}
          value={value}
          onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
          required={field.required}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="v3-ticket-form">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading ticket form...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v3-ticket-form">
        <div className="alert alert-warning" role="alert">
          <strong>Ticket Form Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!event || visibleCategories.length === 0) {
    return (
      <div className="v3-ticket-form">
        <div className="alert alert-info" role="alert">
          No ticket categories are currently available for this event.
        </div>
      </div>
    );
  }

  return (
    <form 
      action={`/events/${event.slug}/register/`}
      id="registration-form"
      className="v3-ticket-form"
      method="post"
      onSubmit={handleSubmit}
    >
      {/* Category Selection */}
      {visibleCategories.length === 1 ? (
        <>
          <input 
            type="hidden"
            id="event_pass_category"
            name="event_pass_category"
            value={visibleCategories[0].id}
          />
          <h5 className="text-center text-primary mb-1">{visibleCategories[0].name}</h5>
          <p className="text-center">
            <strong>{formatPrice(visibleCategories[0])}</strong>
            {event.setting?.show_remaining_passes && (
              <span className="ms-4">
                <i className="fas fa-ticket-alt me-1"></i>
                {visibleCategories[0].remaining} Left
              </span>
            )}
            {visibleCategories[0].sold_out && (
              <span className="text-warning ms-2">(sold out)</span>
            )}
          </p>
        </>
      ) : (
        <>
          <h5 className="text-center mb-3">Select Category</h5>
          <div className="mb-3 d-flex justify-content-center">
            <div className="category-btn-group" role="group" aria-label="Categories">
              {visibleCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`btn btn-primary btn-category ${selectedCategory?.id === category.id ? 'active' : ''}`}
                  data-category-id={category.id}
                  data-category-name={category.name}
                  onClick={() => handleCategorySelect(category)}
                  disabled={category.sold_out}
                >
                  <div className="category-name">{category.name}</div>
                  <div className="category-price">{formatPrice(category)}</div>
                  {category.sold_out && (
                    <div className="sold-out-label">(sold out)</div>
                  )}
                </button>
              ))}
            </div>
            <input 
              type="hidden" 
              id="event_pass_category"
              name="event_pass_category"
              value={selectedCategory?.id || ''}
            />
          </div>
        </>
      )}

      {/* Personal Information Fields */}
      <div className="form-group">
        <label htmlFor="name">Name</label>
        <input
          type="text"
          className="form-control"
          name="name"
          id="name"
          placeholder="Pass Holder Name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          className="form-control"
          name="email"
          id="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <InternationalPhoneInput
          value={formData.phone}
          onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
          placeholder="Phone Number"
          id="phone"
          name="phone"
          label=""
          showLabel={false}
          required={true}
        />
      </div>

      {/* Dynamic Fields */}
      {event.fields?.map((fieldItem: any) => {
        const field = fieldItem.field || fieldItem;
        return renderCustomField(field);
      })}

      {/* Quantity Selection */}
      <div className="form-group">
        <label htmlFor="quantity">Quantity</label>
        <select
          className="form-select"
          name="quantity"
          id="quantity"
          value={quantity}
          onChange={handleQuantityChange}
          disabled={quota === 0 || selectedCategory?.sold_out}
          required
        >
          {Array.from({ length: Math.min(selectedCategory?.remaining || quota || 10, 10) }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
      </div>

      {/* Additional Attendees */}
      {event?.setting?.collect_individual_information && quantity > 1 && (
        <div id="additional-attendees-container">
          <div id="additional-attendees">
            {additionalAttendees.map((attendee, index) => (
              <div key={index} className="additional-attendee mt-4 p-3">
                <h5 className="mb-3">Additional Attendee Information</h5>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name={`name_${index + 2}`}
                    value={attendee.name}
                    onChange={(e) => handleAdditionalAttendeeChange(index, 'name', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name={`email_${index + 2}`}
                    value={attendee.email}
                    onChange={(e) => handleAdditionalAttendeeChange(index, 'email', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <InternationalPhoneInput
                    value={attendee.phone}
                    onChange={(value) => handleAdditionalAttendeeChange(index, 'phone', value || '')}
                    placeholder="Phone Number"
                    id={`phone_${index + 2}`}
                    name={`phone_${index + 2}`}
                    label=""
                    showLabel={false}
                    required={false}
                  />
                </div>
                {event.fields?.map((fieldItem: any) => {
                  const field = fieldItem.field || fieldItem;
                  const fieldName = field.name;
                  const value = attendee.custom_fields[fieldName] || '';

                  if (field.field_type === 'select') {
                    const options = field.options ? field.options.split('\n') : [];
                    return (
                      <div key={`${field.id}_${index}`} className="form-group">
                        <label>{field.label}</label>
                        <select
                          className="form-select"
                          name={`${fieldName}_${index + 2}`}
                          value={value}
                          onChange={(e) => handleAdditionalAttendeeCustomFieldChange(index, fieldName, e.target.value)}
                          required={field.required}
                        >
                          <option value="">Select {field.label}</option>
                          {options.map((option: string, optIndex: number) => (
                            <option key={optIndex} value={option.trim()}>{option.trim()}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  return (
                    <div key={`${field.id}_${index}`} className="form-group">
                      <label>{field.label}</label>
                      <input
                        type={field.field_type}
                        className="form-control"
                        name={`${fieldName}_${index + 2}`}
                        placeholder={field.placeholder || field.label}
                        value={value}
                        onChange={(e) => handleAdditionalAttendeeCustomFieldChange(index, fieldName, e.target.value)}
                        required={field.required}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons and Messages */}
      <div className="text-center mt-4">
        {quota === 0 && (
          <>
            <h6 className="text-warning text-center mt-2">
              You've reached the quota for this event. Check your{' '}
              <a href="/cart/" className="text-primary">cart</a> to see if you already have tickets.
            </h6>
          </>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          id="submit-btn"
          disabled={quota === 0 || !selectedCategory || selectedCategory.sold_out || isSubmitting}
        >
          {isSubmitting ? 'Processing...' : (event.content?.button_label || 'Register')}
        </button>
      </div>

      {submitError && (
        <div className="alert alert-danger mt-3" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {submitError}
        </div>
      )}
    </form>
  );
};

export default V3TicketForm;

